import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  CircularProgress,
  alpha,
  Tooltip,
} from "@mui/material";
import { Checkbox } from "../../ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { AppointmentContext } from "../../../stores/Appointment/appointment.provider";
import useStore from "../../../helpers/useStore";
import { AppointmentQueryFilters, PatientDto, TimeSlotDto } from "../../../api-client";
import TerminView, { formatGap } from "./TerminView";
import { TerminViewConstants } from "../TerminView";
import { formatDateUI, formatTime } from "../../../helpers/dateFormat";
import { backgroundColourCheck, getColorForRxNumber } from "../../../helpers/statusColor";
import { VisitTypeBadge } from "../../../atoms/Chip";
import { getStatusColor } from "./RxForms/statusColor";
import { AssignmentLate, AssignmentTurnedIn, EventBusy } from "@mui/icons-material";
import moment from "moment";
import { usePatient } from "../../../stores/Patients/patient.queries";
import { mobiliTheme } from "../../../themes/mobiliTheme";
import { useFilteredAppointmentsByPatient } from "../../../stores/Appointment/appointment.queries";
import { slotTherapy } from "../../../helpers/slotTherapy";

const FILTERS: { name: string; label: string }[] = [
  { name: "showOnlyFutureAppointments", label: "Zukünftige Termine" },
  { name: "showOnlyActiveRxs", label: "Aktive Rezepte" },
  { name: "rxIds", label: "Gewähltes Rezept" },
  { name: "showLastThreeMonths", label: "Letzte 3 Monate" },
  { name: "personalAppointmentsOnly", label: "Personentermine" },
];

/**
 * Cell padding is tight; this is a data table, not a settings page.
 * `whitespace-nowrap` because every column but the notes holds a short atomic
 * value — an Rx number broken across two lines ("17333-" / "3") is unreadable,
 * and wrapping is what made the old chips look like they needed to be chips.
 * The container scrolls instead.
 */
const CELL = "px-2 py-0.5 align-middle whitespace-nowrap";

/**
 * The documentation column is the only free-text one, so it is the only one
 * that can set the row height. It gets a single line with an ellipsis and the
 * width to make that line worth reading; the full note is on the tooltip.
 * A fixed max-width rather than a percentage: `text-overflow` needs a
 * resolvable width, which an auto-layout table cell does not give it.
 */
const NOTE_MAX_WIDTH = 520;

/** `h-auto` is required: shadcn's TableHead ships `h-12`, a 48px header row. */
const HEAD =
  "h-auto px-2 py-1 text-left text-micro font-semibold uppercase tracking-wide text-ink-muted whitespace-nowrap";

/**
 * Icons in this table default to MUI's 24px — nearly twice the type size, and
 * the tallest thing in a row, so they were what actually set the row height
 * (not the Rx rail, which is a border and stretches to whatever height exists).
 * Sized to `--row-icon` like every other dense surface. The badge's own price
 * tag is matched one level deeper inside VisitTypeBadge, so it stays
 * proportional rather than inheriting this.
 */
const DENSE_ICON_SX = { "& .MuiSvgIcon-root": { fontSize: "var(--row-icon)" } };

interface AppointmentOverviewModalProps {
  open: boolean;
  onClose: () => void;
  patientId: number;
  noFilters?: boolean;
  selectedRxId?: string;
}

const getSlotGapInDays = (
  appointments: any[],
  appointmentId: string,
  slotId: string
): number | null => {
  //  Find current appointment
  const currentAppointment = appointments.find((a) => a?.id === appointmentId);
  if (!currentAppointment) return null;

  //  Find current slot
  const currentSlot = currentAppointment.timeSlots?.find((s: any) => s.id === slotId);
  if (!currentSlot?.start) return null;

  //  Sort future appointments by start date
  const futureAppointments = appointments
    .filter((a) => a?.start && moment(a.start).isAfter(moment(currentAppointment.start)))
    .sort((a, b) => moment(a.start).valueOf() - moment(b.start).valueOf());

  //  Find FIRST appointment that contains same series slot
  for (const appointment of futureAppointments) {
    const matchingSlot = appointment.timeSlots?.find(
      (s: any) =>
        s.treatmentIndex === currentSlot.treatmentIndex ||
        s.therapyRx?.rx?.rxSeries?.id === currentSlot.therapyRx?.rx?.rxSeries?.id
    );

    if (matchingSlot?.start) {
      //  Calculate gap
      return moment(matchingSlot.start)
        .startOf("day")
        .diff(moment(currentSlot.start).startOf("day"), "days");
    }
  }

  return null;
};

const AppointmentOverviewModal: React.FC<AppointmentOverviewModalProps> = ({
  open,
  onClose,
  patientId,
  noFilters,
  selectedRxId,
}) => {
  const { data: patientData } = usePatient(patientId);

  const initialFilters: AppointmentQueryFilters = {
    showOnlyFutureAppointments: false,
    showOnlyActiveRxs: true,
    showLastThreeMonths: false,
    rxIds: selectedRxId ? [selectedRxId] : [],
  };

  const [appointmentFilters, setAppointmentFilters] =
    useState<AppointmentQueryFilters>(initialFilters);
  const { data: appointmentsData, isLoading } = useFilteredAppointmentsByPatient(
    patientId,
    appointmentFilters,
    false,
    true
  );
  const appointments = appointmentsData || [];
  const handleClose = () => {
    setAppointmentFilters(initialFilters); // Reset to initial filters
    onClose();
  };

  // When modal opens, update filters based on `noFilters`
  useEffect(() => {
    if (open) {
      setAppointmentFilters({
        showOnlyFutureAppointments: false,
        showOnlyActiveRxs: noFilters ? false : true,
        showLastThreeMonths: false,
        rxIds: selectedRxId ? [selectedRxId] : [],
      });
    }
  }, [noFilters, open, selectedRxId]);

  // When filters change, fetch appointments
  // useEffect(() => {
  //   if (!open) return; // Only fetch when modal is open

  //   const fetchData = async () => {
  //     try {
  //       setAppointmentsLoading(true);
  //       const response = await getFilteredAppointmentsForPatient(
  //         patientId!,
  //         appointmentFilters,
  //         false
  //       );
  //       if (response) setAppointmentsPatient(response);
  //     } catch (error) {
  //       console.error("Error fetching appointments:", error);
  //     } finally {
  //       setAppointmentsLoading(false);
  //     }
  //   };

  //   fetchData();
  // }, [appointmentFilters]);

  // Handle filter changes
  const handleFilterChange = (name: string, checked: boolean) => {
    if (name === "rxIds") {
      setAppointmentFilters((prevFilters) => ({
        ...prevFilters,
        rxIds: checked ? (selectedRxId ? [selectedRxId] : []) : [],
      }));
    } else {
      setAppointmentFilters((prevFilters) => ({
        ...prevFilters,
        [name]: checked,
      }));
    }
  };

  // Get unique therapists from appointment time slots
  const uniqueTherapists = [
    ...new Map(
      (appointments ? appointments.flatMap((appt) => appt.timeSlots || []) : [])
        .map((slot) => slot.user)
        .filter((user: any) => user) // Ensure `user` exists
        .map((user: any) => [user.id, user]) // Use Map to ensure uniqueness by `id`
    ).values(),
  ];

  const getValidStartDate = (appointment: any, index: number): Date => {
    if (index >= appointments.length - 1) {
      return appointment.start; // Return the start date of the appointment if it's the last one
    }
    return !appointment.end
      ? getValidStartDate(appointments[index + 1], index + 1)
      : appointment.start;
  };

  const columns = [
    { id: "gap", label: "Lücke", width: "6%" },
    { id: "date", label: "Datum", width: "10%" },
    { id: "time", label: "Uhrzeit", width: "8%" },
    { id: "rxNumber", label: "Rezept", width: "10%" },
    { id: "rxDate", label: "Rezeptdatum", width: "9%" },
    { id: "treatmentIndex", label: "Index", width: "6%" },
    { id: "therapy", label: "Therapie", width: "9%" },
    { id: "therapist", label: "Therapeut", width: "12%" },
    { id: "visit", label: "Besuchsart", width: "5%" },
    { id: "status", label: "Status", width: "5%" },
    { id: "treatmentNotes", label: "Doku", width: "5%" },
    { id: "treatmentNotesDetail", label: "Dokumentation", width: "30%" },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <span className="text-ink-text">Terminübersicht</span>{" "}
        <span className="text-ink-muted">
          — {patientData?.firstName} {patientData?.lastName} ({patientData?.id})
        </span>
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          margin: "4px",
        }}
      >
        {/*
          Label column, then content column. `auto 1fr` sizes the label column
          to the widest label, so "Filter" and "Therapeuten" share one right
          edge and their content shares one left edge — the alignment falls out
          of the grid rather than being a hardcoded width to keep in sync.
        */}
        <div
          className="grid items-baseline gap-x-3 gap-y-1.5"
          style={{ gridTemplateColumns: "auto minmax(0, 1fr)" }}
        >
          <span className="text-dense font-semibold text-ink-muted">Filter</span>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {FILTERS.map((filter) => {
              const checked =
                filter.name === "rxIds"
                  ? Boolean(appointmentFilters.rxIds && appointmentFilters.rxIds.length > 0)
                  : Boolean((appointmentFilters as any)[filter.name]);
              const disabled = filter.name === "rxIds" && !selectedRxId;

              return (
                <label
                  key={filter.name}
                  className={[
                    "flex items-center gap-1.5 text-dense",
                    disabled ? "text-ink-muted opacity-60" : "cursor-pointer text-ink-text",
                  ].join(" ")}
                >
                  <Checkbox
                    checked={checked}
                    disabled={disabled}
                    onCheckedChange={(value) => handleFilterChange(filter.name, value === true)}
                    className="h-3.5 w-3.5"
                  />
                  {filter.label}
                </label>
              );
            })}
          </div>

          <span className="text-dense font-semibold text-ink-muted">Therapeuten</span>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
            {uniqueTherapists.length > 0 ? (
              uniqueTherapists.map((therapist: any, index: number) => (
                <span
                  key={therapist?.id ?? `therapist-${index}`}
                  className="text-dense text-ink-text"
                >
                  {therapist ? `${therapist.firstName} ${therapist.lastName}` : "-"}
                </span>
              ))
            ) : (
              <span className="text-dense text-ink-muted">
                Für die aktuellen Filter wurden keine Therapeuten gefunden.
              </span>
            )}
          </div>
        </div>

        {/* Scrollable table */}
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
            <CircularProgress />
          </Box>
        ) : appointments.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
            <span className="text-dense text-ink-muted">Keine Termine gefunden</span>
          </Box>
        ) : (
          <div className="mt-3 overflow-auto rounded-card border border-solid border-ink-edge bg-card">
            {/*
              Inline, not `className="text-dense"`. shadcn's Table sets
              `text-sm` and merges through `cn()` → tailwind-merge, whose
              default config does not know our custom `text-*` steps — so
              `text-dense` is kept ALONGSIDE `text-sm` rather than replacing it,
              and `text-sm` wins on output order. The 20px line-height that
              came with it was holding every row open.
            */}
            <Table style={{ fontSize: "var(--text-dense)", lineHeight: 1.25 }}>
              <TableHeader>
                <TableRow className="border-0 border-b border-solid border-ink-line-strong hover:bg-transparent">
                  {columns.map((col) => (
                    <TableHead key={col.id} className={HEAD} style={{ width: col.width }}>
                      {col.label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {appointments?.map((appointment: any, index) => {
                  const nextAppointment = appointments[index + 1];
                  const gapInDays = (slot: TimeSlotDto) =>
                    getSlotGapInDays(appointments, appointment?.id, slot?.id!);

                  // A rule between ISO weeks, so a long list stays readable.
                  const weekDifference = nextAppointment
                    ? moment(nextAppointment.start)
                        .startOf("isoWeek")
                        .diff(moment(appointment.start).startOf("isoWeek"), "weeks")
                    : 0;
                  const needsDivider = weekDifference > 0;

                  return (
                    <React.Fragment key={index}>
                      {appointment.timeSlots && appointment.timeSlots.length > 0 ? (
                        appointment.timeSlots.map((slot: any, slotIndex: number) => {
                          const rxId = slot?.therapyRx?.rx?.id;
                          const isLastInRx = !appointments.some(
                            (a, i) =>
                              i > index &&
                              a.timeSlots?.some(
                                (slotInner: TimeSlotDto) =>
                                  rxId === slotInner.therapyRx?.rx?.id &&
                                  slotInner.start &&
                                  slotInner.end
                              )
                          );
                          const slotGapInDays = gapInDays(slot);
                          const baseGapColor = backgroundColourCheck(
                            typeof slotGapInDays === "number" ? slotGapInDays / 7 : 0
                          );
                          const gapColor = slot.start
                            ? isLastInRx
                              ? alpha(baseGapColor, 0.5)
                              : baseGapColor
                            : undefined;

                          const rxNumber = slot.therapyRx?.rx?.rxNumber;
                          const therapy = (slotTherapy(slot) as any)?.abbreviation;

                          return (
                            <TableRow
                              key={`${index}-${slotIndex}`}
                              className="border-0 border-b border-solid border-ink-line-faint hover:bg-ink-band"
                            >
                              {/* Gap: a dot carries the severity colour the 30px
                                  filled avatar used to, at a tenth of the ink. */}
                              <TableCell className={CELL}>
                                {slotGapInDays !== null && slotGapInDays !== undefined ? (
                                  <span className="flex items-center gap-1.5">
                                    <span
                                      className="h-2 w-2 shrink-0 rounded-pill bg-ink-muted"
                                      style={{ backgroundColor: gapColor }}
                                    />
                                    <span className="text-dense tabular-nums text-ink-text">
                                      {slotGapInDays}
                                    </span>
                                  </span>
                                ) : (
                                  <span className="text-dense text-ink-muted">–</span>
                                )}
                              </TableCell>

                              {slotIndex === 0 && (
                                <TableCell
                                  rowSpan={appointment.timeSlots.length}
                                  className={`${CELL} align-top text-dense font-semibold tabular-nums text-ink-text`}
                                >
                                  {appointment.start && !appointment.unschedule
                                    ? formatDateUI(appointment.start)
                                    : "–"}
                                </TableCell>
                              )}

                              <TableCell className={`${CELL} text-dense tabular-nums`}>
                                {slot.start ? (
                                  <span className="text-ink-text">{formatTime(slot.start)}</span>
                                ) : (
                                  <span className="text-ink-muted">nicht geplant</span>
                                )}
                              </TableCell>

                              {/* The Rx colour as a 4px rail, the way every other
                                  surface now carries it — consecutive rows on one
                                  Rx merge into a single bar. */}
                              <TableCell
                                className={`${CELL} border-0 border-l-4 border-solid text-dense tabular-nums text-ink-text`}
                                style={{
                                  borderLeftColor: rxNumber
                                    ? getColorForRxNumber(rxNumber)
                                    : "transparent",
                                }}
                              >
                                {rxNumber || "–"}
                              </TableCell>

                              <TableCell className={`${CELL} text-dense tabular-nums text-ink-muted`}>
                                {slot.therapyRx?.rx?.date ? (
                                  moment(slot.therapyRx.rx.date).format("DD.MM.YYYY")
                                ) : (
                                  <Tooltip title="Kein Datum">
                                    <EventBusy
                                      sx={{
                                        fontSize: "var(--row-icon)",
                                        color: "text.disabled",
                                        display: "block",
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </TableCell>

                              <TableCell className={`${CELL} text-dense tabular-nums text-ink-muted`}>
                                {slot.treatmentIndex || "–"}
                              </TableCell>

                              <TableCell className={`${CELL} text-dense font-semibold text-ink-text`}>
                                {therapy || "–"}
                              </TableCell>

                              <TableCell className={`${CELL} text-dense text-ink-text`}>
                                {slot?.user?.id
                                  ? `${slot.user.firstName} ${slot.user.lastName}`
                                  : "–"}
                              </TableCell>

                              <TableCell className={CELL}>
                                <VisitTypeBadge
                                  visitType={appointment?.address?.type}
                                  visitFee={slot?.visitFee?.abbreviation}
                                  sx={DENSE_ICON_SX}
                                />
                              </TableCell>

                              <TableCell className={CELL}>
                                <Tooltip title={`${slot.therapyRx?.rx?.processingStatus || "–"}`}>
                                  <span
                                    className="block h-2 w-2 rounded-pill bg-ink-muted"
                                    style={{
                                      backgroundColor: getStatusColor(
                                        slot.therapyRx?.rx?.processingStatus
                                      ),
                                    }}
                                  />
                                </Tooltip>
                              </TableCell>

                              <TableCell className={CELL}>
                                {slot.attended &&
                                  (slot.treatmentNote ? (
                                    <Tooltip title={slot.treatmentNote.note}>
                                      <AssignmentTurnedIn
                                        sx={{
                                          fontSize: "var(--row-icon)",
                                          color: "success.main",
                                          display: "block",
                                        }}
                                      />
                                    </Tooltip>
                                  ) : (
                                    <Tooltip title="Dokumentation fehlt">
                                      <AssignmentLate
                                        sx={{
                                          fontSize: "var(--row-icon)",
                                          color: "error.main",
                                          display: "block",
                                        }}
                                      />
                                    </Tooltip>
                                  ))}
                              </TableCell>

                              <TableCell className={`${CELL} text-dense text-ink-muted`}>
                                {slot.treatmentNote ? (
                                  <Tooltip title={slot.treatmentNote.note} placement="top" arrow>
                                    <span
                                      className="block overflow-hidden text-ellipsis whitespace-nowrap"
                                      style={{ maxWidth: NOTE_MAX_WIDTH }}
                                    >
                                      {slot.treatmentNote.note}
                                    </span>
                                  </Tooltip>
                                ) : (
                                  "–"
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow key={index}>
                          <TableCell colSpan={columns.length} className={`${CELL} text-dense text-ink-muted`}>
                            Keine Behandlungen gefunden
                          </TableCell>
                        </TableRow>
                      )}

                      {/* Week break. Was a 2–6px grey[600] bar; a hairline in the
                          ink ramp separates just as well without the weight. */}
                      {needsDivider && (
                        <TableRow className="hover:bg-transparent">
                          <TableCell
                            colSpan={columns.length}
                            className="border-0 border-b border-solid border-ink-line-strong p-0"
                            style={{ height: Math.min(4 + 4 * (weekDifference - 1), 16) }}
                          />
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="text">
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AppointmentOverviewModal;
